import { useEffect, useMemo, useState } from "react";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Checkbox, ListItemText } from "@material-ui/core";

import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import DownArrow from "../../assets/icon/DownArrow";

const useStyles = makeStyles((theme) => ({
	chipsContainer: {
		display: "flex",
		overflowX: "auto",
		maxWidth: "100%",
		padding: "2px 0",
	},
	chip: {
		margin: theme.spacing(0.5),
	},
	select: {
	  "&.MuiOutlinedInput-root": {
		"& fieldset": {
		  borderColor: "#00000014",
		},
		"&:hover fieldset": {
		  borderColor: "#00000014",
		},
		"&.Mui-focused fieldset": {
		  borderColor: "#00000014",
		},
	  },
	},
	loadingContainer: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: theme.spacing(2),
	},
	list: {
		width: '100%',
		maxHeight: 300,
		overflow: 'auto',
	},
	listItem: {
		padding: theme.spacing(0, 2),
		'&:hover': {
			backgroundColor: theme.palette.select.hover,
		},
		'&.Mui-selected': {
  	  backgroundColor: theme.palette.select.selected,
  	  '&:hover': {
  	    backgroundColor: theme.palette.select.selected,
  	  },
  	},
	},
	icon: {
		right: theme.spacing(1),
		position: "absolute",
		pointerEvents: "none",
	},
	checkbox: {
		padding: 0,
		marginRight: theme.spacing(1),
		'& .MuiSvgIcon-root': {
			display: 'none'
		}
	},
	selectedItem: {
		color: theme.palette.primary.main,
		fontWeight: 'bold'
	}
}));

const getQueueDisplayOrder = (item) =>
	item?.WhatsappQueue?.displayOrder ??
	item?.whatsappQueue?.displayOrder ??
	item?.displayOrder ??
	0;

/** Soporta cola completa (`id`) o registro pivot WhatsappQueue (`queueId`). */
const getQueueId = (item) => item?.queueId ?? item?.id;

/** Convierte IDs ordenados (orden de selección) al payload con displayOrder (1-based). */
export const queueIdsToDisplayOrderPayload = (queueIds = []) =>
	queueIds.map((queueId, index) => ({
		queueId,
		displayOrder: index + 1,
	}));

/** Restaura el orden guardado al cargar desde el API (colas o pivot WhatsappQueue). */
export const queuesToOrderedIds = (queues = []) =>
	[...queues]
		.sort((a, b) => getQueueDisplayOrder(a) - getQueueDisplayOrder(b))
		.map(getQueueId)
		.filter((id) => id != null);

const QueueSelect = ({ selectedQueueIds, onChange }) => {
	const classes = useStyles();
	const theme = useTheme();
	const [queues, setQueues] = useState([]);
	const [loading, setLoading] = useState(true);

	const isLight = useMemo(() => theme.palette.type === "light", [theme.palette.type]);

	useEffect(() => {
		const fetchQueues = async () => {
			try {
				const { data } = await api.get("/queue");
				console.log('data queue', data)
				setQueues(Array.isArray(data) ? data : []);
			} catch (err) {
				toastError(err);
				setQueues([]);
			} finally {
				setLoading(false);
			}
		};
		fetchQueues();
	}, []);

	const handleChange = ({ target: { value } }) => {
		const newIds = typeof value === "string" ? value.split(",") : value;
		const prevIds = selectedQueueIds || [];
		const added = newIds.filter((id) => !prevIds.includes(id));
		const removed = prevIds.filter((id) => !newIds.includes(id));

		let orderedIds = prevIds.filter((id) => !removed.includes(id));
		orderedIds = [...orderedIds, ...added];

		onChange(orderedIds);
	};

	const selectedQueues = useMemo(
		() =>
			(selectedQueueIds || [])
				.map((id) => queues.find((queue) => queue.id === id))
				.filter(Boolean),
		[queues, selectedQueueIds]
	);

	return (
		<div style={{ marginTop: "2px" }}>
			<FormControl fullWidth margin="dense" variant="outlined">
				<InputLabel>{i18n.t("queueSelect.inputLabel")}</InputLabel>
				{loading ? (
					<div className={classes.loadingContainer}>
						<CircularProgress size={24} />
					</div>
				) : (
					<Select
						multiple
						value={selectedQueueIds || []}
						onChange={handleChange}
						labelWidth={120}
						className={classes.select}
						renderValue={() => (
							<div className={classes.chipsContainer}>
								{selectedQueues.map((queue, index) => (
									<Chip
										key={queue.id}
										label={`${index + 1}.- ${queue.name}`}
										variant="outlined"
										style={{
											border: `2px solid ${queue.color}`,
											color: isLight ? queue.color : theme.palette.textPrimary,
										}}
										className={classes.chip}
									/>
								))}
							</div>
						)}
						IconComponent={() => (
							<div className={classes.icon}>
								<DownArrow fill={theme.palette.messageIcons} />
							</div>
						)}
						MenuProps={{
							anchorOrigin: {
								vertical: "bottom",
								horizontal: "left",
							},
							transformOrigin: {
								vertical: "top",
								horizontal: "left",
							},
							getContentAnchorEl: null,
						}}
					>
						{queues.map((queue) => (
							<MenuItem
								key={queue.id}
								value={queue.id}
								button
								dense
								className={classes.listItem}
							>
								<Checkbox checked={(selectedQueueIds || []).includes(queue.id)} />
								<ListItemText primary={queue.name} />
							</MenuItem>
						))}
					</Select>
				)}
			</FormControl>
		</div>
	);
};

export default QueueSelect;
